import React from "react";
import { useTranslation } from "react-i18next";
import type { UserGroupMember } from "../types/user-groups.types";
import Button from "@/components/ui/button";
import Dropdown from "@/components/ui/dropdown";
import {
  Department as GroupIcon,
  EmployeeRemove as RemoveMemberIcon,
  Plus as PlusIcon,
  User as UserIcon,
} from "@/components/icons";
import styles from "./UserGroupMembersPanel.module.scss";

export interface UserSearchOption {
  label: string;
  value: string;
}

interface UserGroupMembersPanelProps {
  groupName?: string;
  members: UserGroupMember[];
  canUpdate: boolean;
  personnelOptions: UserSearchOption[];
  selectedPersonnelIds: string[];
  selectedPersonnelLabel?: string;
  isSearchingPersonnel?: boolean;
  isLoadingMorePersonnel?: boolean;
  hasMorePersonnel?: boolean;
  isAddingMembers?: boolean;
  removingMemberId?: string | null;
  onPersonnelSearch: (value: string) => void;
  onPersonnelSelect: (value: string[]) => void;
  onLoadMorePersonnel?: () => Promise<boolean>;
  onAddMembers: () => void;
  onRemoveMember: (member: UserGroupMember) => void;
}


const UserGroupMembersPanel: React.FC<UserGroupMembersPanelProps> = ({
  groupName,
  members,
  canUpdate,
  personnelOptions,
  selectedPersonnelIds,
  selectedPersonnelLabel,
  isSearchingPersonnel = false,
  isLoadingMorePersonnel = false,
  hasMorePersonnel = false,
  isAddingMembers = false,
  removingMemberId = null,
  onPersonnelSearch,
  onPersonnelSelect,
  onLoadMorePersonnel,
  onAddMembers,
  onRemoveMember,
}) => {
  const { t } = useTranslation();

  return (
    <section className={styles.panel}>
      <div className={styles.header}>
        <div className={styles.headerIcon}>
          <GroupIcon width={20} height={20} />
        </div>
        <div>
          <h3 className={styles.title}>{t("userGroups.members.title")}</h3>
          <p className={styles.subtitle}>
            {groupName
              ? t("userGroups.members.subtitleWithGroup", { name: groupName })
              : t("userGroups.members.subtitle")}
          </p>
        </div>
      </div>

      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h4 className={styles.sectionTitle}>{t("userGroups.members.addTitle")}</h4>
          <p className={styles.sectionHint}>{t("userGroups.members.addHint")}</p>
        </div>
        <div className={styles.addRow}>
          <div className={styles.dropdownWrap}>
            <Dropdown
              label={t("userGroups.members.personnelField")}
              placeholder={
                isSearchingPersonnel
                  ? t("userGroups.members.searching")
                  : t("userGroups.members.personnelPlaceholder")
              }
              options={personnelOptions}
              value={selectedPersonnelIds}
              selectedLabel={selectedPersonnelLabel}
              onSelect={(value) =>
                onPersonnelSelect(Array.isArray(value) ? value : value ? [value] : [])
              }
              onSearch={onPersonnelSearch}
              hasMore={hasMorePersonnel}
              isLoadingMore={isLoadingMorePersonnel}
              onScrollEnd={onLoadMorePersonnel}
              searchable
              multiple
              allowClear
              disabled={!canUpdate}
            />
          </div>
          <Button
            variant="contained"
            color="primary"
            onClick={onAddMembers}
            disabled={!canUpdate || selectedPersonnelIds.length === 0 || isAddingMembers}
            startIcon={<PlusIcon width={16} height={16} color="#fff" />}
          >
            {isAddingMembers ? t("buttons.loading") : t("userGroups.members.addButton")}
          </Button>
        </div>
      </div>

      <div className={styles.section}>
        <div className={styles.sectionHeaderRow}>
          <div>
            <h4 className={styles.sectionTitle}>{t("userGroups.members.listTitle")}</h4>
            <p className={styles.sectionHint}>{t("userGroups.members.listHint")}</p>
          </div>
          <span className={styles.countBadge}>
            {t("userGroups.members.count", { count: members.length })}
          </span>
        </div>

        {members.length === 0 ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>
              <UserIcon width={20} height={20} />
            </div>
            <div>
              <p className={styles.emptyTitle}>{t("userGroups.members.emptyTitle")}</p>
              <p className={styles.emptyText}>{t("userGroups.members.emptyText")}</p>
            </div>
          </div>
        ) : (
          <div className={styles.memberList}>
            {members.map((member) => {
              const memberName =
                [member.firstName, member.lastName].filter(Boolean).join(" ") ||
                member.email ||
                member.identityNumber ||
                member.id;
              const deptName =
                !member.department
                  ? "—"
                  : typeof member.department === "string"
                    ? member.department || "—"
                    : member.department.name || "—";
              const detailParts = [member.title, deptName].filter(
                (value) => value && value !== "—",
              );
              return (
                <div key={member.id} className={styles.memberCard}>
                  <div className={styles.memberMain}>
                    <div className={styles.memberAvatar}>
                      {memberName.slice(0, 1).toUpperCase()}
                    </div>
                    <div className={styles.memberInfo}>
                      <div className={styles.memberNameRow}>
                        <span className={styles.memberName}>{memberName}</span>
                        {member.identityNumber ? (
                          <span className={styles.inlineBadge}>
                            {member.identityNumber}
                          </span>
                        ) : null}
                      </div>
                      {detailParts.length > 0 && (
                        <div className={styles.memberMeta}>
                          {detailParts.join(" • ")}
                        </div>
                      )}
                    </div>
                  </div>
                  {canUpdate && (
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => onRemoveMember(member)}
                      disabled={removingMemberId === member.id}
                      startIcon={
                        <RemoveMemberIcon width={16} height={16} color="#dc2626" />
                      }
                    >
                      {removingMemberId === member.id
                        ? t("buttons.loading")
                        : t("userGroups.members.removeButton")}
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

export default UserGroupMembersPanel;
